import { useDropzone } from 'react-dropzone'
import PropTypes from 'prop-types'
import every from 'lodash/every'
import styled from 'styled-components'
import { P, Wrapper } from '@farewill/ui'
import { BORDER, COLOR, FONT, GTR } from '@farewill/ui/tokens'
import type { UploadFile } from './types'
import { UploadIcon } from 'components/custom-icons'

const StyledDropZone = styled(Wrapper)<{ $isValid: boolean }>`
  border-color: ${(props) =>
    props.$isValid ? COLOR.GREY.DARK : COLOR.STATE.ERROR};
  border-radius: ${BORDER.RADIUS.M};
  border-style: dashed;
  border-width: 1px;
  cursor: pointer;
  margin: ${GTR.L} 0 0;
  outline: none;
  padding: ${GTR.L} ${GTR.XL};
`

const StyledUploadHeader = styled(P)`
  font-weight: ${FONT.WEIGHT.BOLD};
  font-size: ${FONT.SIZE.L};
`

const StyledBrowseLink = styled(P)`
  display: inline-flex;
  align-items: center;
  gap: ${GTR.XS};
`

const StyledUploadIcon = styled(UploadIcon)`
  margin-right: ${GTR.S};
`

const allImages = (files: File[]) =>
  every(files, (file) => file.type.startsWith('image/'))

const isValidSelection = (files: File[]) =>
  files.length === 1 || allImages(files)

const DropZone = ({ onDrop }: { onDrop: (files: UploadFile[]) => void }) => {
  const handleDrop = (files: unknown) => {
    if (isValidSelection(files as File[])) {
      onDrop(files as UploadFile[])
    }
  }

  const { getRootProps, getInputProps, isDragActive, draggedFiles } =
    useDropzone({
      onDrop: handleDrop,
      noKeyboard: true,
      noClick: false,
    })

  const isValidDrag = isValidSelection(draggedFiles)

  return (
    <StyledDropZone {...getRootProps({ isDragActive })} $isValid={isValidDrag}>
      <input {...getInputProps()} />
      <Wrapper centered>
        <StyledUploadHeader>
          {!isDragActive && 'Add new document'}

          {isDragActive &&
            (isValidDrag
              ? 'Drop the files here to attach'
              : 'All files must be images')}
        </StyledUploadHeader>
        <StyledBrowseLink
          padding={GTR.M}
          background={COLOR.BACKGROUND.SMOKE}
          borderRadius="M"
        >
          <StyledUploadIcon color={COLOR.GREY.DARK} /> Choose a file or drag and
          drop it here
        </StyledBrowseLink>
      </Wrapper>
    </StyledDropZone>
  )
}

DropZone.propTypes = {
  onDrop: PropTypes.func.isRequired,
}

export default DropZone
